import type { CandidateBot } from './resolveBot';
import { shouldStartHeadless } from './headless';
import type { GetdemoEventEmitter } from './getdemoEvents';

/**
 * `Getdemo.on('availability')` — existe agente para abrir nesta página, para ESTE visitante?
 *
 * Pedido do cliente (09/10/2026): ele precisa decidir se desenha o botão dele ANTES de o visitante
 * clicar. Até aqui a única resposta era o `unavailable` do evento `chat`, que só chega DEPOIS do
 * clique — o visitante aperta e nada acontece.
 *
 * É EVENTO e não uma função de resposta única, e isso não é preferência de estilo. Num ERP o
 * `Getdemo.identify` chega depois do login, às vezes segundos depois do carregamento, e é ele que
 * decide a audiência. Uma pergunta feita no load responderia "não" para o visitante que meio
 * segundo depois PASSA a ter acesso, e o cliente teria escondido o botão de quem tem direito a
 * ele — que é, literalmente, a regressão b738eb5b ("o Copilot sumiu"). Como evento, a resposta se
 * corrige sozinha: o loader republica a cada nova resolução (identify tardio, navegação SPA que
 * entra ou sai das regras de página) e o botão acompanha.
 */

/**
 * "Dá para abrir um chat aqui": há agente elegível e ele tem interface de chat.
 *
 * Um agente só-API (canal `api` sem `widget`) é elegível mas não tem o que abrir, então não conta.
 * Já um agente fixado por `gd-bot-id` que não está no conjunto de candidatos chega aqui com `bot`
 * nulo — e esse nós montamos mesmo assim, então conta como disponível.
 */
export const isChatAvailable = (
  botId: string | null | undefined,
  bot: CandidateBot | null | undefined
): boolean => Boolean(botId) && !(bot && shouldStartHeadless(bot));

export type AvailabilityPublisher = (
  botId: string | null | undefined,
  bot: CandidateBot | null | undefined
) => boolean;

/**
 * Publica a disponibilidade, SÓ quando ela muda.
 *
 * A deduplicação importa porque o loader re-resolve em toda navegação de SPA: sem ela, um ERP que
 * troca de rota o tempo todo entregaria ao cliente uma enxurrada de eventos idênticos, e quem
 * ligou o botão a esse evento ficaria redesenhando à toa.
 *
 * Devolve `true` quando emitiu, para quem quiser logar.
 */
export const createAvailabilityPublisher = (
  emitter: GetdemoEventEmitter
): AvailabilityPublisher => {
  let last: boolean | null = null;
  return (botId, bot) => {
    const available = isChatAvailable(botId, bot);
    if (available === last) return false;
    last = available;
    emitter.emit('availability', { available });
    return true;
  };
};
